<script>
  import {
    Card,
    Table,
    TableHead,
    TableHeadCell,
    TableBody,
    TableBodyRow,
    TableBodyCell,
    ButtonGroup,
    Input,
    Button,
    Modal,
    Spinner,
    Radio,
    Checkbox,
  } from 'flowbite-svelte';
  import Layout from '../components/Layout.svelte';
  import { _ } from 'svelte-i18n';
  import {
    createQuery,
    createMutation,
    useQueryClient,
  } from '@tanstack/svelte-query';
  import {
    CheckSolid,
    ChevronUpOutline,
    ChevronDownOutline,
  } from 'flowbite-svelte-icons';
  import { client } from '../lib/client';
  import { addToast } from '../store';
  import { slide } from 'svelte/transition';
  import {
    TOAST_ERROR,
    TOAST_SUCCESS,
    QUERY_KEY_USER_DATA,
    QUERY_KEY_USER_PROFILE,
  } from '../constants';
  import { queryUserData, updateUserProfile } from '@button/ui/data';

  let instructionsModalOpen = false;
  let creatorTypeConfirmationModalOpen = true;
  let showAllMarkets = false;

  const queryClient = useQueryClient();

  async function getTopMarkets() {
    const result = await client.get('/api/geo/top-markets');
    return result.data;
  }

  const topMarketsQuery = createQuery({
    queryKey: ['top-markets-geolocation'],
    queryFn: getTopMarkets,
  });

  const userQuery = queryUserData();
  $: creatorTypeConfirmationModalOpen =
    $userQuery?.data?.object?.creatorType === null;
  let creatorType = null;
  $: creatorType = $userQuery?.data?.object?.creatorType;

  let isChecked = false;
  let isInitialized = false;
  let isCollapsed = true;
  $: if (!isInitialized) {
    isChecked = $userQuery?.data?.object?.geoMatchPreference === 'exact';
    isInitialized = true;
  }

  const updateProfileMutation = createMutation({
    mutationKey: 'creator-type-update',
    mutationFn: updateUserProfile,
    onSettled: async () => {
      await queryClient.invalidateQueries([
        QUERY_KEY_USER_DATA,
        QUERY_KEY_USER_PROFILE,
      ]);
    },
  });

  async function getGeoConfig() {
    const result = await client.get('/api/geo/config');
    return result.data;
  }

  const geoConfigQuery = createQuery({
    queryKey: ['geo-config'],
    queryFn: getGeoConfig,
  });

  async function updateGeoMatchPreference(geoMatchPreference) {
    const result = await client.patch('/api/geo/update-match-preference', {
      geo_match_preference: geoMatchPreference,
    });
    await queryClient.invalidateQueries([QUERY_KEY_USER_DATA]);
    return result;
  }

  async function upsertMarket({ countryCode, storeId, storeSuffix }) {
    const update =
      !!$geoConfigQuery?.data?.object?.[countryCode] ||
      $geoConfigQuery?.data?.object?.[countryCode] === '';
    const storeIdPattern = /-(\d{2})$/;
    const cleanedStoreId = (storeId || '').trim();
    const currentGeoConfig = $geoConfigQuery?.data?.object || {};
    const existingOtherStoreIds = Object.entries(currentGeoConfig)
      .filter(([key]) => key !== countryCode)
      .map(([, storeId]) => storeId.trim());
    const storeIdMatch = cleanedStoreId.match(storeIdPattern);
    if (cleanedStoreId !== '' && !storeIdMatch) {
      addToast({
        type: TOAST_ERROR,
        message: $_('geolocation.toast.updateMarket.validationError', {
          default:
            'Store ID must end with a hyphen and two digits (e.g., "store-21", "store-20", etc).',
        }),
      });
      return;
    }
    if (
      cleanedStoreId !== '' &&
      storeIdMatch &&
      storeIdMatch[1] !== storeSuffix
    ) {
      addToast({
        type: TOAST_ERROR,
        message: $_('geolocation.toast.updateMarket.validationSuffixError', {
          default:
            'Incorrect suffix for the country. Please ensure you are entering the proper tag.',
        }),
      });
      return;
    }
    if (
      cleanedStoreId !== '' &&
      existingOtherStoreIds.includes(cleanedStoreId)
    ) {
      addToast({
        type: TOAST_ERROR,
        message: $_('geolocation.toast.updateMarket.duplicateStoreIdError', {
          default: 'Store ID already exists in another country.',
        }),
      });
      return;
    }
    if (update) {
      try {
        await client.patch('/api/geo/update-market', {
          market: countryCode,
          store_id: cleanedStoreId,
        });
      } catch (e) {
        addToast({
          type: TOAST_ERROR,
          message: $_('geolocation.toast.updateMarket.error', {
            default:
              'Failed to update. Please check your store ID and try again.',
          }),
        });
        return;
      }

      addToast({
        type: TOAST_SUCCESS,
        message: $_('geolocation.toast.updateMarket', {
          default: 'Success! Your store ID has been updated!',
        }),
      });
    } else {
      try {
        await client.post('/api/geo/add-market', {
          market: countryCode,
          store_id: cleanedStoreId,
        });
      } catch (e) {
        addToast({
          type: TOAST_ERROR,
          message: $_('geolocation.toast.addMarket.error', {
            default:
              'Failed to update. Please check your store ID and try again.',
          }),
        });
        return;
      }
      addToast({
        type: TOAST_SUCCESS,
        message: $_('geolocation.toast.addMarket', {
          default: 'Success! Your store ID has been added!',
        }),
      });
    }
  }

  const upsertGeoMarket = createMutation({
    mutationKey: ['geo-upsert-market'],
    mutationFn: upsertMarket,
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        'top-markets-geolocation',
        'geo-config',
      ]);
    },
  });

  $: topPercent = Math.floor(
    ($topMarketsQuery?.data?.object?.top_markets || []).reduce(
      (acc, elem) => acc + elem.percent_of_total,
      0,
    ),
  );

  function zipMarkets(markets, geoConfig) {
    return markets.map((market) => {
      const hasStore = Object.prototype.hasOwnProperty.call(
        geoConfig,
        market.code,
      );

      if (hasStore) {
        return {
          ...market,
          storeId: geoConfig[market.code],
        };
      }
      return {
        ...market,
        storeId: '',
      };
    });
  }

  let topMarkets = [];
  let otherMarkets = [];
  $: topMarkets =
    $topMarketsQuery.isSuccess && $geoConfigQuery.isSuccess
      ? zipMarkets(
          $topMarketsQuery.data.object.top_markets,
          $geoConfigQuery.data.object || {},
        )
      : [];
  $: otherMarkets =
    $topMarketsQuery.isSuccess && $geoConfigQuery.isSuccess
      ? zipMarkets(
          $topMarketsQuery.data.object.other_markets,
          $geoConfigQuery.data.object || {},
        )
      : [];

  let creatorTypeSelection = null;
</script>

<Modal
  dismissable={false}
  open={creatorTypeConfirmationModalOpen}
  title={$_('geolocation.confirmationModal.title', {
    default: 'Before you begin',
  })}
>
  <div>
    <h2 class="mb-2 font-bold text-black">
      {$_('geolocation.confirmationModal.header', {
        default: 'Tell us how you use PostTap',
      })}
    </h2>
    <p class="mb-6 text-sm">
      {$_('geolocation.confirmationModal.body', {
        default:
          'Certain features are not compatible with PostTap accounts that create links for multiple different creators.',
      })}
    </p>
    <form
      on:submit={(e) => {
        e.preventDefault();
        $updateProfileMutation.mutate({
          creatorType: creatorTypeSelection,
        });
      }}
    >
      <div class="mb-6 flex flex-col gap-4">
        <div class="flex w-full gap-2 rounded-md bg-[#F4F2FF] px-8 py-4">
          <Radio
            id="creator-type-creator"
            bind:group={creatorTypeSelection}
            name="creatorType"
            value="creator"
          />
          <div>
            <label for="creator-type-creator" class="font-bold text-black">
              {$_('geolocation.confirmationModal.creatorType.title', {
                default: "I'm a Creator",
              })}
            </label>
            <p class="text-sm">
              {$_('geolocation.confirmationModal.creatorType.body', {
                default: 'I create links for myself only',
              })}
            </p>
          </div>
        </div>
        <div class="flex w-full gap-2 rounded-md bg-[#F4F2FF] px-8 py-4">
          <Radio
            id="creator-type-manager"
            bind:group={creatorTypeSelection}
            name="creatorType"
            value="manager"
          />
          <div>
            <label for="creator-type-manager" class="font-bold text-black">
              {$_('geolocation.confirmationModal.mangerType.title', {
                default: "I'm a Manager",
              })}
            </label>
            <p class="text-sm">
              {$_('geolocation.confirmationModal.managerType.body', {
                default: 'I manage links for multiple creators',
              })}
            </p>
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end">
        <Button type="submit" disabled={creatorTypeSelection === null}>
          {$_('geolocation.confirmationModal.submit', {
            default: 'Submit',
          })}
        </Button>
      </div>
    </form>
  </div>
</Modal>

<Modal
  size="xl"
  autoclose
  bind:open={instructionsModalOpen}
  title={$_('geolocation.instructions.modal.title', {
    default: 'How to Set Up International Markets',
  })}
>
  <div class="px-8 py-2">
    <ol class="mb-8 flex list-decimal flex-col gap-4 text-black" type="1">
      <li>
        {$_('geolocation.instructions.modal.li1', {
          default:
            'To earn internationally on product links, Amazon requires you to have an Associate’s account in each market your buyers could be clicking from. There are 20 different Associate markets - but to help you prioritize, we’ll show you the top markets for your current international buyers or our recommended markets.',
        })}
      </li>
      <li>
        {$_('geolocation.instructions.modal.li2', {
          default:
            'To create an Associate’s account, click the sign-up link in the far right of the table for each market.',
        })}
      </li>
      <li>
        {$_('geolocation.instructions.modal.li3', {
          default:
            'Complete Amazon’s application process by following the steps on Amazon’s site to complete your application. This typically takes 5-10 minutes per market and you’ll need to provide basic information about your website or social media channels.',
        })}
      </li>
      <li>
        {$_('geolocation.instructions.modal.li5', {
          default:
            'At the end of the application process for each market, Amazon will provide you with a store ID. That’s what you’ll paste into your PostTap dashboard on this page.',
        })}
      </li>
      <li>
        {$_('geolocation.instructions.modal.li7', {
          default:
            'Enter the store ID for each market in PostTap and click ‘Enable’. Going forward, if you receive clicks on your links in any of those markets, we’ll swap the tag from the existing link to match the relevant store ID. You’ll see the relevant commissions and earnings for that market’s store ID in your Amazon Associate’s dashboards.',
        })}
      </li>
      <li>
        {$_('geolocation.instructions.modal.li8', {
          default:
            'Please note: for clicks that you receive from buyers in your own market, we won’t modify the tag in any way. We only swap tags if the buyer is outside of the market for where the link was created.',
        })}
        <div class="my-2 rounded-md bg-gray-100 px-2 py-3 md:mx-4 md:px-6">
          <p class="mb-2 text-gray-600">
            {$_('geolocation.instructions.examples.title', {
              default: 'Examples:',
            })}
          </p>
          <ul class="list-disc px-6">
            <li>
              <p class="font-bold">
                {$_('geolocation.instructions.examples.b1title', {
                  default: 'Will Not Swap Tag',
                })}
              </p>
              <p>
                {$_('geolocation.instructions.examples.b1body', {
                  default:
                    'If you are a US creator who has made a US link that a US buyer has clicked on, we won’t modify the tag at all and simply persist the one you added.',
                })}
              </p>
            </li>
            <li>
              <p class="font-bold">
                {$_('geolocation.instructions.examples.b2title', {
                  default: 'Will Swap Tag',
                })}
              </p>
              <p>
                {$_('geolocation.instructions.examples.b2body', {
                  default:
                    'Non-US buyer clicks on a US link - we’ll modify the tag in the link to match your store ID for that buyer’s market that you’ve set-up. ',
                })}
              </p>
            </li>
          </ul>
        </div>
      </li>
    </ol>
    <div
      class="flex items-center gap-2 rounded-md bg-[#F4F2FF] px-2 py-3 md:px-6"
    >
      <img src="/images/geo-pro-tips.png" class="hidden h-32 w-32 md:block" />
      <div class="md:px-6">
        <p class="mb-3 font-bold text-black">
          {$_('geolocation.instructions.modal.tips.title', {
            default: 'Pro Tips',
          })}
        </p>
        <ul class="flex list-disc flex-col gap-2 px-6 text-sm text-black">
          <li>
            {$_('geolocation.instructions.modal.tips.tip1', {
              default:
                'Start with your top 5 markets to capture the majority of your international traffic',
            })}
          </li>
          <li>
            {$_('geolocation.instructions.modal.tips.tip2', {
              default:
                'Use the same store name prefix across all markets for consistency',
            })}
          </li>
        </ul>
      </div>
    </div>
  </div>
</Modal>
<Layout>
  <h2 class="mb-2 text-xl font-bold">
    {$_('geolocation.title', {
      default: 'Earn from International Shoppers',
    })}
  </h2>
  <p class="mb-4 text-sm text-gray-500">
    {$_('geolocation.subtitle', {
      default:
        'Set up an Amazon Associate account in each Amazon market to capture earnings from International buyers who are clicking your links! Once you complete the set-up flow in Amazon, return here to provide your store-id for each market, and we’ll automatically re-route clicks outside of your market to the local Amazon store for the international shopper. This helps you capture earnings from these clicks and creates a nice experience for your followers!',
    })}
  </p>

  <Card class="mb-6 w-full max-w-none">
    <h3 class="mb-2 font-bold text-black">
      {$_('geolocation.card1.title', {
        default: 'How PostTap’s Smart Geo-Linking Works',
      })}
    </h3>
    <p class="mb-4 text-gray-500">
      {$_('geolocation.card1.subtitle', {
        default:
          "When international visitors click on your Amazon links, we'll automatically handle everything to help you maximize your earnings!",
      })}
    </p>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div class="flex gap-4 rounded-md bg-[#F4F2FF] px-3 py-2">
        <img class="block h-16 w-16 self-center" src="/images/geo-link.png" />
        <div class="flex flex-col justify-start">
          <h4 class="mb-2 font-bold text-black">
            {$_('geolocation.card1.card1.title', {
              default: 'Automatic Market Redirection',
            })}
          </h4>
          <p class="text-sm text-gray-500">
            {$_('geolocation.card1.card1.body', {
              default:
                'We will automatically redirect shoppers to their local Amazon store to create a smooth buyer experience',
            })}
          </p>
        </div>
      </div>
      <div class="flex gap-4 rounded-md bg-[#F4F2FF] px-3 py-2">
        <img
          class="block h-16 w-16 self-center"
          src="/images/geo-crystal.png"
        />
        <div class="flex flex-col justify-start">
          <h4 class="mb-2 font-bold text-black">
            {$_('geolocation.card1.card2.title', {
              default: 'Streamlined Product Matching',
            })}
          </h4>
          <p class="text-sm text-gray-500">
            {$_('geolocation.card1.card2.body', {
              default:
                'International buyers will be rerouted to the exact product if it exists in their local store or to our best match. Configure below.',
            })}
          </p>
        </div>
      </div>
      <div class="flex gap-4 rounded-md bg-[#F4F2FF] px-3 py-2">
        <img class="block h-16 w-16 self-center" src="/images/geo-chart.png" />
        <div class="flex flex-col justify-start">
          <h4 class="mb-2 font-bold text-black">
            {$_('geolocation.card1.card3.title', {
              default: 'More Earnings!',
            })}
          </h4>
          <p class="text-sm text-gray-500">
            {$_('geolocation.card1.card3.body', {
              default:
                'We will automatically update your link for you to include your store-id in each buyer’s market - helping you earn commissions for all international purchases!',
            })}
          </p>
        </div>
      </div>
    </div>
    <div class="py-4">
      <div class="flex w-full flex-col gap-4 bg-[#F9FAFB] px-4 py-2">
        <!-- Collapsible header -->
        <div
          class="flex cursor-pointer items-center justify-between"
          on:click={() => (isCollapsed = !isCollapsed)}
        >
          <h4 class="text-black">
            {$_('geolocation.preferences.title', {
              default: 'Product Matching Preference',
            })}
          </h4>
          <!-- Svelte icon -->
          {#if isCollapsed}
            <ChevronDownOutline class="h-5 w-5 text-gray-500" />
          {:else}
            <ChevronUpOutline class="h-5 w-5 text-gray-500" />
          {/if}
        </div>

        <!-- Collapsible content -->
        {#if !isCollapsed}
          <div transition:slide>
            <h4 class="text-black">
              {$_('geolocation.preferences.exact.title', {
                default:
                  'By default, international buyers will be routed to the exact product in their local store, or to our closest product match.',
              })}
            </h4>
            <div class="mt-2 flex items-center gap-3">
              <Checkbox
                bind:checked={isChecked}
                on:change={() => {
                  if (isInitialized) {
                    setTimeout(() => {
                      updateGeoMatchPreference(isChecked ? 'exact' : 'all');
                    }, 0);
                  }
                }}
                >{$_('geolocation.preferences.exact.text', {
                  default:
                    'Check here to only route buyers when the exact product is available in their market (not recommended)',
                })}</Checkbox
              >
            </div>
          </div>
        {/if}
      </div>
    </div>
  </Card>

  <!-- success card after someone has enrolled -->
  {#if topMarkets.find((elem) => !!elem.storeId) || otherMarkets.find((elem) => !!elem.storeId)}
    {@const earningMarkets =
      topMarkets.reduce((acc, elem) => acc + (elem.storeId ? 1 : 0), 0) +
      otherMarkets.reduce((acc, elem) => acc + (elem.storeId ? 1 : 0), 0)}
    {@const totalMarkets = topMarkets.length + otherMarkets.length}
    <div
      class="mb-4 flex items-center gap-2 rounded-md border border-[#D1D5DB] bg-[#E1FFED] px-3 py-5 md:gap-4 md:px-8"
    >
      <img
        src="/images/geo-green-check.png"
        class="h-10 w-10 md:h-14 md:w-14"
      />
      <div>
        <h3 class="mb-2 text-sm font-bold">
          {$_('geolocation.success.title1', {
            default: "You're earning in",
          })}
          {earningMarkets}
          {$_('geolocation.success.title2', {
            default: 'international market(s)',
          })}
        </h3>
        <p class="text-xs text-gray-600">
          {$_('geolocation.success.body1', {
            default: "You've set up",
          })}
          {earningMarkets}
          {$_('geolocation.success.body2', {
            default: 'of',
          })}
          {totalMarkets}
          {$_('geolocation.success.body3', {
            default: 'available Amazon markets.',
          })}
        </p>
        <p class="text-xs text-gray-600">
          {$_('geolocation.success.note', {
            default:
              'Note: Geo-routing will be enabled only on new links going forward.',
          })}
        </p>
      </div>
    </div>
  {/if}

  {#if creatorType !== 'creator'}
    <Card class="w-full max-w-none">
      <div class="flex flex-col justify-center">
        <h3 class="text-center text-lg font-bold text-black">
          {$_('geolocation.managerCreator.header', {
            default: "Sorry, this feature isn't available yet for managers!",
          })}
        </h3>
        <p class="text-center">
          {$_('geolocation.managerCreator.body', {
            default:
              'Currently, geo-routing is only available for individual creators - we’ll keep you posted once we support accounts who create links for multiple creators.',
          })}
        </p>
      </div>
    </Card>
  {:else}
    <Card class="mb-4 w-full max-w-none">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="font-bold text-black">
          {#if topPercent === 0}
            {$_('geolocation.topmarkets.recTitle', {
              default: 'Top 5 Recommended Markets',
            })}
          {:else}
            {$_('geolocation.topmarkets.title', {
              default: 'Top 5 Clicked Markets',
            })}
          {/if}
        </h3>
        <p class="hidden text-sm text-black md:block">
          {$_('geolocation.topmarkets.subtitle1', {
            default: 'Based on your clicks in the last 30 days',
          })}
        </p>
      </div>
      <div class="border-gray-[#D1D5DB] border-t" />
      {#if !$geoConfigQuery.isSuccess || !$topMarketsQuery.isSuccess}
        <div class="flex h-40 items-center justify-center">
          <Spinner />
        </div>
      {:else}
        <Table>
          <TableHead>
            <TableHeadCell>
              {$_('geolocation.topmarkets.market', {
                default: 'Market',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.topmarkets.traffic', {
                default: 'Traffic',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.topmarkets.status', {
                default: 'Status',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.topmarkets.storeId', {
                default: 'Store ID',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.topmarkets.action', {
                default: 'Action',
              })}
            </TableHeadCell>
          </TableHead>
          <TableBody>
            {#each topMarkets as tm}
              <TableBodyRow>
                <TableBodyCell>
                  <p class="font-bold text-black">
                    {tm.country}
                  </p>
                  <p class="text-sm text-gray-600">
                    {tm.marketplace}
                  </p>
                </TableBodyCell>
                <TableBodyCell>
                  <p class="font-bold text-black">
                    {#if tm.percent_of_total === 0}
                      0%
                    {:else if tm.percent_of_total < 1}
                      &lt;1%
                    {:else}
                      {Math.floor(tm.percent_of_total)}%
                    {/if}
                  </p>
                  <p class="text-sm text-gray-600">
                    {tm.journeys}
                    {$_('geolocation.topmarkets.traffic.clicks', {
                      default: 'clicks',
                    })}
                  </p>
                </TableBodyCell>
                <TableBodyCell>
                  <div class="flex items-center gap-2">
                    {#if tm.storeId}
                      <div class="h-3 w-3 rounded-full bg-green-600" />
                      <p class="font-semibold text-green-600">
                        {$_('geolocation.topmarkets.status.enabled', {
                          default: 'Enabled',
                        })}
                      </p>
                    {:else}
                      <div class="h-3 w-3 rounded-full bg-gray-400" />
                      <p class="font-semibold text-black">
                        {$_('geolocation.topmarkets.status.notEnabled', {
                          default: 'Not Enabled',
                        })}
                      </p>
                    {/if}
                  </div>
                </TableBodyCell>
                <TableBodyCell>
                  {#if tm.storeId}
                    <!-- enabled -->
                    <ButtonGroup class="mb-1">
                      <Input
                        id={`input-row-${tm.code}`}
                        class="border-green-600 text-green-600 focus:border-green-600 focus:ring-0"
                        placeholder={$_(
                          'geolocation.storeId.placeholder.topMarketsSaved',
                          {
                            default: 'Enter Store ID',
                          },
                        )}
                        value={tm.storeId}
                        minlength="1"
                      />
                      <Button
                        class="group border-green-600 text-green-600 hover:border-green-600 hover:text-green-600 focus:text-green-600"
                        on:click={() => {
                          const storeId = document.getElementById(
                            `input-row-${tm.code}`,
                          ).value;
                          $upsertGeoMarket.mutate({
                            countryCode: tm.code,
                            storeId,
                            storeSuffix: tm.store_suffix,
                          });
                        }}
                      >
                        <div class="w-12 group-hover:hidden">
                          {$_('geolocation.storeId.input.topMarketsSaved', {
                            default: 'Saved',
                          })}
                        </div>
                        <div class="hidden w-12 group-hover:inline">
                          {$_('geolocation.storeId.input.topMarketsUpdate', {
                            default: 'Update',
                          })}
                        </div>
                      </Button>
                    </ButtonGroup>
                    <p class="text-xs text-gray-500">
                      {tm.code}
                      {$_('geolocation.storeId.input.topMarketSavedSubtitle', {
                        default: 'Store-IDs end with',
                      })}
                      -{tm.store_suffix}
                    </p>
                  {:else}
                    <!-- not enabled -->
                    <ButtonGroup class="mb-1">
                      <Input
                        id={`input-row-${tm.code}`}
                        placeholder={$_(
                          'geolocation.storeId.placeholder.topMarketEnable',
                          {
                            default: 'Enter Store ID',
                          },
                        )}
                        minlength="1"
                      />
                      <Button
                        on:click={() => {
                          const storeId = document.getElementById(
                            `input-row-${tm.code}`,
                          ).value;
                          $upsertGeoMarket.mutate({
                            countryCode: tm.code,
                            storeId,
                            storeSuffix: tm.store_suffix,
                          });
                        }}
                      >
                        {$_('geolocation.storeId.input.topMarketsEnable', {
                          default: 'Enable',
                        })}
                      </Button>
                    </ButtonGroup>
                    <p class="text-xs text-gray-500">
                      {tm.code}
                      {$_('geolocation.storeId.input.topMarketEnableSubtitle', {
                        default: 'Store-IDs end with',
                      })}
                      -{tm.store_suffix}
                    </p>
                  {/if}
                </TableBodyCell>
                <TableBodyCell>
                  {#if tm.storeId}
                    <!-- enabled -->
                    <div class="flex items-center gap-2">
                      <CheckSolid class="h-4 w-4 text-green-600" />
                      <a
                        href={tm.affiliate_program_link}
                        target="_blank"
                        class="text-green-600"
                      >
                        {$_('geolocation.otherMarkets.action.topMarketsSaved', {
                          default: 'Enabled Amazon',
                        })}
                        {tm.code}
                      </a>
                    </div>
                  {:else}
                    <Button
                      target="_blank"
                      href={tm.affiliate_program_link}
                      class="border border-primary-700 bg-transparent text-primary-700 hover:text-white"
                    >
                      {$_('geolocation.action.topMarketsSignupButton', {
                        default: 'Sign up on Amazon',
                      })}
                      {tm.code}
                    </Button>
                  {/if}
                </TableBodyCell>
              </TableBodyRow>
            {/each}
          </TableBody>
        </Table>
        <div class="py-4">
          <div
            class="flex w-full flex-col items-center justify-center gap-4 bg-[#F9FAFB] px-4 py-8"
          >
            <p class="max-w-[120ch] text-center text-sm text-gray-600">
              {#if topPercent !== 0}
                {$_('geolocation.instructions.callout1', {
                  default: 'Setting up these markets will capture ',
                })}
                <span class="text-gray-800">{topPercent}</span>{$_(
                  'geolocation.instructions.callout2',
                  {
                    default: '% of your international traffic.',
                  },
                )}
              {:else}
                {$_('geolocation.instructions.calloutEmpty', {
                  default:
                    'These are our top 5 recommended markets for you to set-up. Once you start creating and sharing links, you can return here to get insights on which markets drive most of your clicks and set-up more!',
                })}
              {/if}
            </p>
            <Button
              class="border border-primary-700 bg-transparent text-primary-700 hover:text-white"
              on:click={() => {
                instructionsModalOpen = true;
              }}
              >{$_('geolocation.instructions.button', {
                default: 'View Instructions',
              })}
            </Button>
          </div>
        </div>
      {/if}
    </Card>

    <!-- Other Markets -->
    <Card class="w-full max-w-none">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="font-bold text-black">
          {$_('geolocation.otherMarkets.title', {
            default: 'Other Markets',
          })}
        </h3>
        <p class="hidden text-sm text-black md:block">
          {$_('geolocation.otherMarkets.subtitle1', {
            default: 'Based on your clicks in the last 30 days',
          })}
        </p>
      </div>
      <div class="border-gray-[#D1D5DB] border-t" />
      {#if !$geoConfigQuery.isSuccess || !$topMarketsQuery.isSuccess}
        <div class="flex h-40 items-center justify-center">
          <Spinner />
        </div>
      {:else}
        <Table>
          <TableHead>
            <TableHeadCell>
              {$_('geolocation.otherMarkets.market', {
                default: 'Market',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.otherMarkets.traffic', {
                default: 'Traffic',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.otherMarkets.status', {
                default: 'Status',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.otherMarkets.storeId', {
                default: 'Store ID',
              })}
            </TableHeadCell>
            <TableHeadCell>
              {$_('geolocation.otherMarkets.action', {
                default: 'Action',
              })}
            </TableHeadCell>
          </TableHead>
          <TableBody>
            {#each otherMarkets.slice(0, showAllMarkets ? Infinity : 4) as om}
              <TableBodyRow>
                <TableBodyCell>
                  <p class="font-bold text-black">
                    {om.country}
                  </p>
                  <p class="text-sm text-gray-600">
                    {om.marketplace}
                  </p>
                </TableBodyCell>
                <TableBodyCell>
                  <p class="font-bold text-black">
                    {#if om.percent_of_total === 0}
                      0%
                    {:else if om.percent_of_total < 1}
                      &lt;1%
                    {:else}
                      {Math.floor(om.percent_of_total)}%
                    {/if}
                  </p>
                  <p class="text-sm text-gray-600">
                    {om.journeys}
                    {$_('geolocation.otherMarkets.traffic.clicks', {
                      default: 'clicks',
                    })}
                  </p>
                </TableBodyCell>
                <TableBodyCell>
                  <div class="flex items-center gap-2">
                    {#if om.storeId}
                      <div class="h-3 w-3 rounded-full bg-green-600" />
                      <p class="font-semibold text-green-600">
                        {$_('geolocation.otherMarkets.status.enabled', {
                          default: 'Enabled',
                        })}
                      </p>
                    {:else}
                      <div class="h-3 w-3 rounded-full bg-gray-400" />
                      <p class="font-semibold text-black">
                        {$_('geolocation.otherMarkets.status.notEnabled', {
                          default: 'Not Enabled',
                        })}
                      </p>
                    {/if}
                  </div>
                </TableBodyCell>
                <TableBodyCell>
                  {#if om.storeId}
                    <!-- enabled -->
                    <ButtonGroup class="mb-1">
                      <Input
                        id={`input-row-${om.code}`}
                        class="border-green-600 text-green-600 focus:border-green-600 focus:ring-0"
                        placeholder={$_(
                          'geolocation.storeId.placeholder.otherMarketsSaved',
                          {
                            default: 'Enter Store ID',
                          },
                        )}
                        value={om.storeId}
                        minlength="1"
                      />
                      <Button
                        class="group border-green-600 text-green-600 hover:border-green-600 hover:text-green-600 focus:text-green-600"
                        on:click={() => {
                          const storeId = document.getElementById(
                            `input-row-${om.code}`,
                          ).value;
                          $upsertGeoMarket.mutate({
                            countryCode: om.code,
                            storeId,
                            storeSuffix: om.store_suffix,
                          });
                        }}
                      >
                        <div class="w-12 group-hover:hidden">
                          {$_('geolocation.storeId.input.otherMarketsSaved', {
                            default: 'Saved',
                          })}
                        </div>
                        <div class="hidden w-12 group-hover:inline">
                          {$_('geolocation.storeId.input.otherMarketsUpdate', {
                            default: 'Update',
                          })}
                        </div>
                      </Button>
                    </ButtonGroup>
                    <p class="text-xs text-gray-500">
                      {om.code}
                      {$_(
                        'geolocation.storeId.input.otherMarketsSavedSubtitle',
                        {
                          default: 'Store-IDs end with',
                        },
                      )}
                      -{om.store_suffix}
                    </p>
                  {:else}
                    <!-- not enabled -->
                    <ButtonGroup class="mb-1">
                      <Input
                        id={`input-row-${om.code}`}
                        placeholder={$_(
                          'geolocation.storeId.placeholder.otherMarketsEnable',
                          {
                            default: 'Enter Store ID',
                          },
                        )}
                        minlength="1"
                      />
                      <Button
                        on:click={() => {
                          const storeId = document.getElementById(
                            `input-row-${om.code}`,
                          ).value;
                          $upsertGeoMarket.mutate({
                            countryCode: om.code,
                            storeId,
                            storeSuffix: om.store_suffix,
                          });
                        }}
                      >
                        {$_('geolocation.storeId.input.otherMarketsEnable', {
                          default: 'Enable',
                        })}
                      </Button>
                    </ButtonGroup>
                    <p class="text-xs text-gray-500">
                      {om.code}
                      {$_(
                        'geolocation.storeId.input.otherMarketsEnableSubtitle',
                        {
                          default: 'Store-IDs end with',
                        },
                      )}
                      -{om.store_suffix}
                    </p>
                  {/if}
                </TableBodyCell>
                <TableBodyCell>
                  {#if om.storeId}
                    <!-- enabled -->
                    <div class="flex items-center gap-2">
                      <CheckSolid class="h-4 w-4 text-green-600" />
                      <a
                        href={om.affiliate_program_link}
                        target="_blank"
                        class="text-green-600"
                      >
                        {$_('geolocation.otherMarkets.action.saved', {
                          default: 'Enabled Amazon',
                        })}
                        {om.code}
                      </a>
                    </div>
                  {:else}
                    <Button
                      href={om.affiliate_program_link}
                      target="_blank"
                      class="border border-primary-700 bg-transparent text-primary-700 hover:text-white"
                    >
                      {$_('geolocation.otherMarkets.action.signupButton', {
                        default: 'Sign up on Amazon',
                      })}
                      {om.code}
                    </Button>
                  {/if}
                </TableBodyCell>
              </TableBodyRow>
            {/each}
          </TableBody>
        </Table>
      {/if}
      <div class="py-4">
        {#if !showAllMarkets}
          <div class="mb-4 flex flex-col items-center">
            <Button
              on:click={() => {
                showAllMarkets = !showAllMarkets;
              }}
            >
              {$_('geolocation.otherMarkets.showAllMarkets', {
                default: 'Show All Markets',
              })}
            </Button>
          </div>
        {/if}
        <div
          class="flex w-full flex-col items-center justify-center gap-4 bg-[#F9FAFB] px-4 py-8"
        >
          <p class="max-w-[120ch] text-center text-sm text-gray-600">
            {#if topPercent !== 0}
              {$_('geolocation.otherMarkets.bottomText1', {
                default: 'These markets represent less than',
              })}
              {#if otherMarkets.reduce((acc, elem) => elem.percent_of_total + acc, 0) < 1}
                &lt;1
              {:else}
                {Math.floor(
                  otherMarkets.reduce(
                    (acc, elem) => elem.percent_of_total + acc,
                    0,
                  ),
                )}
              {/if}
              {$_('geolocation.otherMarkets.bottomText2', {
                default:
                  '% of your traffic, but you can proactively set them up for future tracking',
              })}
            {:else}
              {$_('geolocation.otherMarkets.bottomTextEmpty', {
                default:
                  'These are other markets for you to set-up. Once you start creating and sharing links, you can return here to get insights on which markets drive most of your clicks and set-up more!',
              })}
            {/if}
          </p>
        </div>
      </div>
    </Card>
  {/if}
</Layout>
